import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { add_to_card } from '../../store/reducers/cardReducer';
import { imgUrl } from '../../utils/imgUrl';
import { DISPLAY, Check, Truck } from '../home/ui';

// Piezas compartidas de "Mi cuenta" y "Rastrea tu pedido" (diseño cuenta.html).

export const Card = ({ className = '', children }) => (
    <div className={`bg-white border border-[#f1efea] rounded-[20px] p-6 sm:p-5 ${className}`}>{children}</div>
);

export const CardHead = ({ title, children }) => (
    <div className='flex justify-between items-center gap-3 mb-4'>
        <h3 className='text-[#1e3a8a] text-[19px] font-bold m-0' style={DISPLAY}>{title}</h3>
        {children}
    </div>
);

export const PageHead = ({ title, sub, children }) => (
    <div className='mb-[22px] flex justify-between items-end gap-3 flex-wrap'>
        <div>
            <h1 className='text-[#1e3a8a] font-bold m-0 tracking-[-.02em] leading-[1.1]' style={{ ...DISPLAY, fontSize: 'clamp(26px, 3vw, 34px)' }}>{title}</h1>
            {sub && <p className='mt-[6px] mb-0 text-[#64748b] text-[15px] max-w-[620px]'>{sub}</p>}
        </div>
        {children}
    </div>
);

const PILL = {
    ok: 'bg-[#dcfce7] text-[#15803d]',
    go: 'bg-[#ecfeff] text-[#0e7490]',
    wait: 'bg-[#fef3c7] text-[#b45309]',
    no: 'bg-[#fee2e2] text-[#b91c1c]',
    mute: 'bg-[#f1f5f9] text-[#475569]',
};
export const Pill = ({ kind = 'mute', children, big = false }) => (
    <span className={`inline-flex items-center gap-[6px] rounded-full font-bold whitespace-nowrap ${big ? 'text-[13px] px-[14px] py-[7px]' : 'text-[12px] px-[11px] py-[5px]'} ${PILL[kind]}`}>
        <span className='w-[6px] h-[6px] rounded-full bg-current' />{children}
    </span>
);

// Estados de ECOMMERCE_ORDER.STATUS (C-02) → etiqueta, color, paso y grupo.
const STATUS = {
    pending:   { label: 'Pendiente',  kind: 'wait', step: 0, group: 'proc', headline: 'Recibimos tu pedido' },
    confirmed: { label: 'Confirmado', kind: 'go',   step: 1, group: 'proc', headline: 'Tu pedido está confirmado' },
    preparing: { label: 'Preparando', kind: 'wait', step: 1, group: 'proc', headline: 'Estamos preparando tu pedido' },
    shipped:   { label: 'En camino',  kind: 'go',   step: 2, group: 'proc', headline: 'Tu pedido va en camino' },
    delivered: { label: 'Entregado',  kind: 'ok',   step: 4, group: 'ok', headline: 'Tu pedido fue entregado' },
    cancelled: { label: 'Cancelado',  kind: 'no',   step: -1, group: 'no', headline: 'Este pedido se canceló' },
};
export const orderStatus = (s) => STATUS[(s || '').toLowerCase()] || { label: s || '—', kind: 'mute', step: 0, group: 'proc', headline: 'Tu pedido' };

const PAY = {
    pending:   { label: 'Pendiente', kind: 'wait' },
    confirmed: { label: 'Pagado',    kind: 'ok' },
    rejected:  { label: 'Rechazado', kind: 'no' },
};
export const payStatus = (s) => PAY[(s || '').toLowerCase()] || { label: s || '—', kind: 'mute' };

export const fecha = (iso, opts = { day: 'numeric', month: 'short', year: 'numeric' }) => {
    if (!iso) return '';
    const d = new Date(iso.length <= 10 ? `${iso}T12:00:00` : iso);
    return isNaN(d) ? '' : d.toLocaleDateString('es-GT', opts);
};

export const orderLabel = (o) => o?.orderId || (o?._id ? `#${o._id}` : '');

const HomeIcon = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5 12 3l9 6.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" /></svg>
);
const BoxIcon = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 8 12 3 3 8v8l9 5 9-5z" /><path d="M3 8l9 5 9-5" /><path d="M12 13v8" /></svg>
);
const ClipIcon = (p) => (
    <svg {...p} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="2" width="8" height="4" rx="1" /><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" /></svg>
);
export { BoxIcon };

const STEP_LABELS = ['Confirmado', 'Preparado', 'En camino', 'Entregado'];
const STEP_ICONS = [ClipIcon, BoxIcon, Truck, HomeIcon];

/* Barra de 4 pasos. `status` es delivery_status del backend. */
export const Steps = ({ status }) => {
    const { step } = orderStatus(status);
    return (
        <div className='grid grid-cols-4 relative my-2'>
            {STEP_LABELS.map((l, i) => {
                const done = step === 4 || i < step;
                const cur = !done && i === step;
                const Icon = STEP_ICONS[i];
                return (
                    <div key={l} className={`flex flex-col items-center text-center gap-2 relative text-[12.5px] font-semibold ${done || cur ? 'text-[#0f172a]' : 'text-[#64748b]'}`}>
                        {i > 0 && <span className={`absolute top-[15px] -left-1/2 right-1/2 h-[3px] z-0 ${done || cur ? 'bg-[#0891b2]' : 'bg-[#e8e6e1]'}`} />}
                        <span className={`w-8 h-8 rounded-full grid place-items-center relative z-[1] border-2 ${done ? 'bg-[#0891b2] border-[#0891b2] text-white' : cur ? 'bg-white border-[#0891b2] text-[#0891b2] shadow-[0_0_0_5px_rgba(8,145,178,.14)]' : 'bg-white border-[#e8e6e1] text-[#64748b]'}`}>
                            {done ? <Check className='w-[15px] h-[15px]' strokeWidth={3} /> : <Icon className='w-[15px] h-[15px]' />}
                        </span>
                        {l}
                    </div>
                );
            })}
        </div>
    );
};

export const Thumb = ({ src, size = 52 }) => (
    <div className='rounded-[12px] overflow-hidden flex-shrink-0 border border-[#f1efea] bg-[#faf9f6] grid place-items-center' style={{ width: size, height: size }}>
        {src ? <img src={imgUrl(src)} alt='' className='w-full h-full object-cover' /> : <span className='text-[18px] opacity-30'>🐾</span>}
    </div>
);

export const gbtn = 'h-11 px-[18px] rounded-[12px] border-[1.5px] border-[#e8e6e1] bg-white text-[#1e3a8a] font-bold text-[14px] inline-flex items-center gap-2 transition-all hover:border-[#1e3a8a] whitespace-nowrap';
export const pbtn = 'h-[50px] px-[26px] rounded-[12px] bg-[#0891b2] hover:bg-[#0e7490] text-white font-bold text-[15px] inline-flex items-center justify-center gap-2 transition-all disabled:opacity-45 disabled:cursor-not-allowed';

/* Detalle de varios pedidos (productos), pedido por pedido. La lista del
   backend no trae los productos; el detalle sí. */
export const useOrderDetails = (ids) => {
    const [details, setDetails] = useState({});
    const key = ids.join(',');
    useEffect(() => {
        let vivo = true;
        const faltan = ids.filter(id => !details[id]);
        if (!faltan.length) return;
        Promise.all(faltan.map(id =>
            api.get(`/orders/detail/${id}`).then(r => [id, r.data.order]).catch(() => [id, null])
        )).then(pares => {
            if (vivo) setDetails(d => ({ ...d, ...Object.fromEntries(pares) }));
        });
        return () => { vivo = false; };
    }, [key]);
    return details;
};

/* "Volver a pedir": agrega al carrito las mismas variantes y cantidades. */
export const useReorder = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userInfo } = useSelector(state => state.auth);
    return (order) => {
        if (!userInfo) { navigate('/login'); return; }
        (order?.products || []).forEach(p =>
            dispatch(add_to_card({ userId: userInfo.id, quantity: p.quantity || 1, productId: p.id }))
        );
    };
};

export const EmptyCard = ({ icon, title, text, children }) => (
    <Card className='text-center !py-12'>
        <div className='w-[60px] h-[60px] rounded-full bg-[#ecfeff] text-[#0891b2] grid place-items-center mx-auto mb-[14px]'>{icon}</div>
        <h3 className='text-[#1e3a8a] text-[20px] font-bold mt-0 mb-[6px]' style={DISPLAY}>{title}</h3>
        <p className='text-[#64748b] text-[14.5px] mt-0 mb-5'>{text}</p>
        {children}
    </Card>
);
